import { CustomFieldEntityEnum } from '@monetize/types/app';
import { useMemo, useState } from 'react';
import { MAccordion, MBox, MFlex } from '../../../../components/Monetize';
import { ExpandCollapseButton } from '../../../../components/Monetize/ExpandCollapseButton';
import { useCustomFields } from '../../../../hooks/useCustomFields';
import { OneTimeInvoiceAdditionalFields } from './OneTimeInvoiceAdditionalFields';
import { OneTimeInvoiceBasic } from './OneTimeInvoiceBasic';
import { OneTimeInvoiceProducts } from './OneTimeInvoiceProducts';
import { OneTimeInvoiceTermsAndMemo } from './OneTimeInvoiceTermsAndMemo';

/**
 * Section layout for the one-time invoice form, mirroring the internal quote
 * page: the setup card sits in its own accordion, the remaining sections share
 * a multi-expand accordion driven by the expand/collapse all control.
 */
export const OneTimeInvoiceFormBody = () => {
  // Products is open by default, Additional Fields and Terms and Memo are not.
  // Each section's Accordion.Item `value` is its stringified render position
  // (v3 requires an explicit value, no implicit index-based identity like
  // v2), threaded down as a `value` prop to each section component below.
  const [openAccordionValue, setOpenAccordionValue] = useState(['0']);

  // The additional fields section is hidden when no invoice custom fields
  // exist, so downstream accordion positions shift accordingly
  const { customFieldList } = useCustomFields(CustomFieldEntityEnum.INVOICE);
  const hasCustomFields = customFieldList.length > 0;

  const accordionItemCount = hasCustomFields ? 3 : 2;
  const allAccordionValues = useMemo(
    () =>
      Array.from({ length: accordionItemCount }, (_, index) => String(index)),
    [accordionItemCount],
  );

  const isAccordionExpanded =
    openAccordionValue.length === allAccordionValues.length;

  const onToggleAccordion = () => {
    if (isAccordionExpanded) {
      setOpenAccordionValue([]);
    } else {
      setOpenAccordionValue(allAccordionValues);
    }
  };

  return (
    <MBox w="100%">
      <MAccordion
        w="100%"
        my={4}
        collapsible
        variant="outline"
        defaultValue={['0']}
      >
        <OneTimeInvoiceBasic value="0" />
      </MAccordion>

      <MFlex alignItems="center" mb="1" justifyContent="flex-start">
        <ExpandCollapseButton
          isExpanded={isAccordionExpanded}
          onChange={onToggleAccordion}
        />
      </MFlex>

      <MBox
        overflow="hidden"
        marginBottom="100px" // Give some space below
      >
        <MAccordion
          multiple
          p={1}
          variant="bottomBorder"
          value={openAccordionValue}
          onValueChange={({ value }) => setOpenAccordionValue(value)}
        >
          <OneTimeInvoiceProducts value="0" />
          {hasCustomFields && <OneTimeInvoiceAdditionalFields value="1" />}
          <OneTimeInvoiceTermsAndMemo value={hasCustomFields ? '2' : '1'} />
        </MAccordion>
      </MBox>
    </MBox>
  );
};
